import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clientsApi, scopesApi } from '../services/api';

export default function Dashboard() {
  const [clients, setClients] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([clientsApi.getAll(), scopesApi.getAll()])
      .then(([c, s]) => {
        setClients(c);
        setScopes(s);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const activeClients = clients.filter((c) => c.isActive).length;
  const activeScopes = scopes.filter((s) => s.isActive).length;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Overview of your OAuth2 authorization server</p>
      </div>

      {error && <div className="error-banner">Connection error: {error}. Check your settings.</div>}

      {loading ? (
        <div className="loading-state">Loading...</div>
      ) : (
        <>
          <div className="row mb-4">
            <div className="col-md-3 mb-3">
              <div className="stat-card">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="stat-value">{clients.length}</div>
                    <div className="stat-label">Total Clients</div>
                  </div>
                  <span className="stat-icon">&#128193;</span>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="stat-card">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="stat-value" style={{ color: '#065f46' }}>{activeClients}</div>
                    <div className="stat-label">Active Clients</div>
                  </div>
                  <span className="stat-icon">&#9989;</span>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="stat-card">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="stat-value">{scopes.length}</div>
                    <div className="stat-label">Total Scopes</div>
                  </div>
                  <span className="stat-icon">&#128274;</span>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="stat-card">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="stat-value" style={{ color: '#065f46' }}>{activeScopes}</div>
                    <div className="stat-label">Active Scopes</div>
                  </div>
                  <span className="stat-icon">&#128275;</span>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-7 mb-4">
              <div className="data-card">
                <div className="data-card-header">
                  <h5>Recent Clients</h5>
                  <Link to="/clients" style={{ fontSize: 13, color: '#6390e8' }}>View all</Link>
                </div>
                {clients.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">&#128193;</div>
                    <div>No clients yet. <Link to="/clients">Create one</Link>.</div>
                  </div>
                ) : (
                  <table className="table admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Client ID</th>
                        <th>Status</th>
                        <th>Scopes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.slice(0, 5).map((c) => (
                        <tr key={c.id}>
                          <td><strong>{c.name}</strong></td>
                          <td><span className="client-id-mono">{c.clientId}</span></td>
                          <td>
                            <span className={c.isActive ? 'badge-active' : 'badge-inactive'}>
                              {c.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            {c.scopes.slice(0, 3).map((s) => (
                              <span key={s} className="scope-tag">{s}</span>
                            ))}
                            {c.scopes.length > 3 && (
                              <span className="scope-tag">+{c.scopes.length - 3}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="col-md-5 mb-4">
              <div className="data-card">
                <div className="data-card-header">
                  <h5>Available Scopes</h5>
                  <Link to="/scopes" style={{ fontSize: 13, color: '#6390e8' }}>Manage</Link>
                </div>
                {scopes.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">&#128274;</div>
                    <div>No scopes yet. <Link to="/scopes">Create one</Link>.</div>
                  </div>
                ) : (
                  <div style={{ padding: '12px 16px' }}>
                    {scopes.map((s) => (
                      <div key={s.id} className="d-flex align-items-center justify-content-between py-2"
                        style={{ borderBottom: '1px solid #f0f2f7' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                          {s.description && (
                            <div style={{ fontSize: 12, color: '#8892a4' }}>{s.description}</div>
                          )}
                        </div>
                        <span className={s.isActive ? 'badge-active' : 'badge-inactive'}>
                          {s.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
